import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;
  #revision = 0;
  #intent = 0;
  #emptyCloseTimer;
  #layoutFrame = 0;
  #resizeObserver;
  #contentObserver;
  #observedSummary;

  #cancelEmptyClose = () => {
    clearTimeout(this.#emptyCloseTimer);
  };

  #handleDrawerClose = () => {
    this.#intent += 1;
    this.#cancelEmptyClose();
  };

  #queueLayout = () => {
    cancelAnimationFrame(this.#layoutFrame);
    this.#layoutFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const summary = this.querySelector('.cart-drawer__summary');
      if (summary !== this.#observedSummary) {
        if (this.#observedSummary) this.#resizeObserver?.unobserve(this.#observedSummary);
        if (summary) this.#resizeObserver?.observe(summary);
        this.#observedSummary = summary;
      }
      this.#updateStickyState();
    });
  };

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    this.#themeDrawer?.addEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#resizeObserver = new ResizeObserver(this.#queueLayout);
    if (this.#dialog) this.#resizeObserver.observe(this.#dialog);
    this.#contentObserver = new MutationObserver(this.#queueLayout);
    this.#contentObserver.observe(this, { childList: true, subtree: true });
    this.#queueLayout();

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#themeDrawer?.removeEventListener(DrawerCloseEvent.eventName, this.#handleDrawerClose);
    this.#revision += 1;
    this.#cancelEmptyClose();
    cancelAnimationFrame(this.#layoutFrame);
    this.#resizeObserver?.disconnect();
    this.#contentObserver?.disconnect();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#intent += 1;
    this.#cancelEmptyClose();
    this.#queueLayout();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    this.#cancelEmptyClose();
    const revision = ++this.#revision;
    const intent = this.#intent;
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add';
    const sourceModal = event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    event.promise?.then(async ({ detail }) => {
      if (detail?.didError) return;
      await new Promise(requestAnimationFrame);
      if (!this.isConnected || revision !== this.#revision || intent !== this.#intent) return;
      this.#queueLayout();
      let count = null;
      if (Array.isArray(detail?.items) && detail.items.every((item) => Number.isInteger(item.quantity) && item.quantity >= 0)) {
        count = detail.items.reduce((total, item) => total + item.quantity, 0);
      } else if (detail?.source === 'cart-items-component' && Number.isInteger(detail.itemCount)) {
        count = detail.itemCount;
      }

      if (count === 0 && ['remove', 'update'].includes(event.action)) {
        const delay = matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 240;
        this.#emptyCloseTimer = setTimeout(() => {
          if (!this.isConnected || revision !== this.#revision || intent !== this.#intent) return;
          if (this.querySelector('.cart-items__table-row')) return;
          if (this.#themeDrawer?.isOpen) this.#themeDrawer.close();
        }, delay);
        return;
      }
      if (!shouldAutoOpen || count === 0) return;
      const openAndSettle = () => {
        if (!this.isConnected || revision !== this.#revision || intent !== this.#intent) return;
        if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
        this.#queueLayout();
      };
      if (sourceModal?.open && sourceModal !== this.#dialog) {
        sourceModal.addEventListener('close', openAndSettle, { once: true });
      } else {
        openAndSettle();
      }
    }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
    });
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (drawerHeight <= 0) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
